import {
  Anchor,
  Button,
  Center,
  Input,
  PinInput,
  Stack,
  Text,
  Title
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { useInterval } from '@mantine/hooks'
import { KeyboardEvent, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

const OTP_LENGTH = 6
const RESEND_TIMEOUT = 60

type OtpResendButtonProps = {
  resendCountdown: number
  onResend: () => void
}

const OtpResendButton = ({
  resendCountdown,
  onResend
}: OtpResendButtonProps) => {
  if (resendCountdown > 0) {
    return (
      <Text size="sm" ta="center">
        <FormattedMessage
          id="login.otp.resend.countdown"
          values={{ countdown: resendCountdown }}
        />
      </Text>
    )
  }

  return (
    <Anchor size="sm" component="button" type="button" onClick={onResend}>
      <FormattedMessage id="login.otp.resend" />
    </Anchor>
  )
}

type FormValues = {
  otp: string
}

type OtpFormProps = {
  isLoading?: boolean
  email: string
  onSubmit: (otp: string) => void
  onResend: () => void
}

export const OtpForm = ({
  isLoading,
  email,
  onSubmit,
  onResend
}: OtpFormProps) => {
  const intl = useIntl()
  const [resendCountdown, setResendCountdown] = useState(0)

  const interval = useInterval(() => {
    setResendCountdown((current) => {
      if (current <= 1) {
        interval.stop()
        return 0
      }

      return current - 1
    })
  }, 1000)

  const form = useForm<FormValues>({
    initialValues: {
      otp: ''
    },

    validate: {
      otp: (value) =>
        value.length === OTP_LENGTH
          ? null
          : intl.formatMessage({ id: 'login.otp.required' })
    }
  })

  const handleResend = () => {
    if (resendCountdown === 0) {
      onResend()
      form.setFieldValue('otp', '')
      setResendCountdown(RESEND_TIMEOUT)
      interval.start()
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !isLoading && form.isValid()) {
      onSubmit(form.getValues().otp)
    }
  }

  return (
    <form onSubmit={form.onSubmit((values) => onSubmit(values.otp))}>
      <Stack gap="lg" mb={60}>
        <Title order={2}>
          <FormattedMessage id="login.otp.title" />
        </Title>

        <Text size="sm">
          <FormattedMessage
            id="login.otp.description"
            values={{
              i: (chunks) => <i>{chunks}</i>,
              email,
              length: OTP_LENGTH
            }}
          />
        </Text>
      </Stack>

      <Stack gap="lg" align="center">
        <Center w="100%" bg="gray.0" p="md">
          <Input.Wrapper error={form.errors.otp}>
            <PinInput
              disabled={isLoading}
              size="lg"
              length={OTP_LENGTH}
              inputMode="numeric"
              autoFocus
              onKeyDown={handleKeyDown}
              {...form.getInputProps('otp')}
            />
          </Input.Wrapper>
        </Center>

        <Button
          disabled={!form.isValid()}
          type="submit"
          loading={isLoading}
          loaderProps={{ type: 'dots' }}
          fullWidth
        >
          <FormattedMessage id="continue" />
        </Button>

        {!isLoading && (
          <OtpResendButton
            resendCountdown={resendCountdown}
            onResend={handleResend}
          />
        )}
      </Stack>
    </form>
  )
}
